import _extends from "@babel/runtime/helpers/extends";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { LogActionId } from "@smartsheet/logging-state-service";
import React, { forwardRef, useEffect, useMemo, useRef } from "react";
import { uid } from "react-uid";
import { useTheme } from "../../theme/utils/useTheme";
import { useLoggingDecorator } from "../../util/logging";
import { Checkmark } from "./components/Checkmark";
import { checkboxCssVars, CheckboxIcon } from "./styled/CheckboxIcon";
import { CheckboxLabel } from "./styled/CheckboxLabel";
import { CheckmarkWrapper } from "./styled/CheckmarkWrapper";
import { Input } from "./styled/Input";
import { LabelText } from "./styled/LabelText";
const noopChangeHandler = () => {
  // This is provided to the onChange handler of the input. It's only
  // here because React throws out a warning if you don't include it AND the value prop.
  // However, we do something special with the onClick event in the label
  // so we're all good.
  // Quiet please, React. ty.
};

/**
 * Checkbox: select single values for form submission.
 */
export const Checkbox = /*#__PURE__*/forwardRef((_ref, parentRef) => {
  let {
    appearance = "check",
    "aria-hidden": ariaHidden,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedBy,
    onBlur,
    onFocus,
    onClick,
    checkedState,
    className,
    inputId,
    isDisabled = false,
    isInvalid = false,
    isRequired = false,
    omitHiddenInput = false,
    label,
    size = "large",
    tabIndex,
    name,
    clientId,
    value
  } = _ref;
  const localRef = useRef(null);
  const ref = useForkRef(parentRef, localRef);
  const withLogging = useLoggingDecorator({
    clientId: clientId,
    // TODO pull from logging-state-service
    controlType: "cbx"
  });
  if (process.env.NODE_ENV !== "production") {
    if (size !== "large" && label !== undefined) {
      console.warn("Medium Checkboxes cannot be used with the label prop. Add an aria-label or aria-labelledby to label it.");
    }
  }
  const {
    key
  } = useTheme();
  const themeMode = key;
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const handleClick = event => {
    if (isDisabled) {
      return;
    }

    // The default action of a label around an input is to re-trigger the click event on the
    // input element. We need to ignore the first event on the label here or we'll call this
    // handler twice since the event will be re-fired on the input as well.
    // We could preventDefault in the first event, but for screen-reader support we must
    // have the event on the input element be handled, or the SR won't say "Space, checked".
    // It would just say "Space" and not provide the new state.
    if (label && event.target !== localRef.current) {
      event.stopPropagation();
      return;
    }
    return withLogging.enabledByDefault({
      actionId: LogActionId.CLICK
    }, onClick)(event);
  };
  const sizeNum = size === "large" ? 20 : 16;
  const id = useMemo(() => inputId || uid({
    id: "Checkbox"
  }), [inputId]);

  // If there's no text label prop provided, this component will fail
  // some a11y scanners due to there being an empty <label>. This happens
  // even when an aria-label is provided anyways since that is stuck on
  // the inner input and the label is still empty.
  // To prevent a breaking change, we've hidden this in the onClick
  // signature but we'll eventually need to fix this with a major release.
  // See https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/387
  const Root = useMemo(() => label ? CheckboxLabel : CheckboxLabel.withComponent("div"), [label]);
  useEffect(() => {
    if (localRef.current) {
      localRef.current.indeterminate = checkedState === "indeterminate";
    }
  }, [checkedState]);
  return /*#__PURE__*/React.createElement(Root, {
    htmlFor: label ? id : undefined,
    "data-client-id": clientId,
    className: className,
    onClick: handleClick,
    css: checkboxCssVars(sizeNum)
  }, /*#__PURE__*/React.createElement(CheckboxIcon, {
    appearance: appearance,
    themeMode: themeMode,
    size: sizeNum
  }, /*#__PURE__*/React.createElement(Input, _extends({
    "aria-hidden": ariaHidden,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedBy,
    "aria-disabled": isDisabled,
    checked: checkedState === "checked",
    id: id,
    name: name,
    onBlur: onBlur,
    onFocus: onFocus,
    onChange: noopChangeHandler,
    ref: ref,
    required: isRequired,
    tabIndex: tabIndex,
    hidden: omitHiddenInput,
    type: "checkbox"
  }, value !== undefined && {
    value
  }, {
    "data-client-id": clientId && `${clientId}--input`
    // This is a marker attribute that we use to style stuff.
  }, isInvalid && {
    "data-is-invalid": true
  })), /*#__PURE__*/React.createElement(CheckmarkWrapper, {
    size: sizeNum,
    themeMode: themeMode
    // This is a marker attribute that we use to style stuff.
    ,
    "data-checkbox-wrapper": true
  }, /*#__PURE__*/React.createElement(Checkmark, {
    appearance: appearance,
    checkedState: checkedState,
    size: sizeNum,
    themeMode: themeMode
  }))), label && size === "large" && /*#__PURE__*/React.createElement(LabelText, {
    isDisabled: isDisabled,
    themeMode: themeMode
  }, label));
});
Checkbox.displayName = "Checkbox";
//# sourceMappingURL=Checkbox.js.map